import React from 'react'
import * as Sentry from '@sentry/node'
import Script from 'next/script'
import {AppProps} from 'next/app'
import {appWithTranslation} from 'next-i18next'
import {Amplify} from '@aws-amplify/core'

import {TemplatesThemeProvider} from '@miro-site/templates-design'
import {getAnalytics} from '@miro-site/analytics'
import {SplitIoProvider} from '@miro-site/splitio'
import {useHotjar} from '@miro-site/core'

import nextI18nextConfig from '../next-i18next.config'
import awsExports from '../src/aws-exports'
import useRouterScroll from '../src/hooks/useRouterScroll'
import useAnalytics from '../src/hooks/useAnalytics'

import '../src/utils/polyfills'
import '../public/static/styles/one-trust-cookie-banner.css'

Amplify.configure({...awsExports})

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    enabled: Boolean(Number(process.env.NEXT_PUBLIC_SENTRY_ENABLE)),
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  })
}

const Templates = (props: AppProps): JSX.Element => {
  const {Component, pageProps, err} = props as AppProps & {
    err: unknown
  }

  if (process.env.NEXT_PUBLIC_HOTJAR_ID) {
    const hotJarId = +process.env.NEXT_PUBLIC_HOTJAR_ID
    const hotJarVersion = +process.env.NEXT_PUBLIC_HOTJAR_VERSION || 6
    const hotJarDebugMode = !!process.env.NEXT_PUBLIC_HOTJAR_DEBUG_MODE

    if (hotJarId) {
      useHotjar(hotJarId, hotJarVersion, hotJarDebugMode)
    }
  }

  useRouterScroll()
  useAnalytics()

  const content = (
    <TemplatesThemeProvider>
      <Script
        strategy="beforeInteractive"
        src={`${process.env.NEXT_PUBLIC_ASSET_PREFIX || ''}/static/scripts/optanonWrapper.js`}
      />

      <Script
        src="https://cdn.cookielaw.org/consent/4133a0ef-6999-461d-909d-24f2b05bbc15/otSDKStub.js"
        data-document-language="true"
        charSet="UTF-8"
        data-domain-script="4133a0ef-6999-461d-909d-24f2b05bbc15"
        strategy="beforeInteractive"
      />

      <Component {...pageProps} err={err} />
    </TemplatesThemeProvider>
  )

  if (process.env.NEXT_PUBLIC_SPLITIO_CLIENT_KEY) {
    return (
      <SplitIoProvider
        authorizationKey={process.env.NEXT_PUBLIC_SPLITIO_CLIENT_KEY}
        getAnalytics={getAnalytics}
      >
        {content}
      </SplitIoProvider>
    )
  }

  return content
}

export default appWithTranslation(Templates, nextI18nextConfig)
